// node_modules
import React, { useState, useEffect, useRef, createContext } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useDispatch } from "react-redux";
import { PrimeReactProvider } from "primereact/api";

// pages
import ChefPortal from "./pages/Chef/ChefPortal";
import StudentPortal from "./pages/Student/StudentPortal";
// import StudentLogin from "./pages/Student/StudentLogin";
import ManagerPortal from "./pages/Manager/ManagerPortal";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import Login from "./pages/Login/Login";
import StatusScreen from "./pages/StatusScreen/StatusScreen";

// redux
import { useGetVersionQuery } from "./redux/services/app";

// components
import Header from "./components/Header";
import { setUser } from "./redux/slices/user.slice";
import PrivateRoute from "./components/PrivateRoute";
import Loader from "./components/Loader/Loader";

// primereact components
import { Toast } from "primereact/toast";

// styles
import * as styles from "./App.module.css";
import { ImportModalProvider } from "./context/ImportModalContext";
import StudentLogin from "./pages/Student/StudentLogin";
import GoogleCallback from "./pages/Auth/GoogleCallback";

export const ToastContext = createContext();

const TokenHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const access = params.get("access_token");
    if (!access) return;
    const refresh = params.get("refresh_token");
    const profileStr = params.get("profile") || params.get("user") || null;

    try {
      localStorage.setItem("access_token", access);
      if (refresh) localStorage.setItem("refresh_token", refresh);

      if (profileStr) {
        try {
          const p = JSON.parse(decodeURIComponent(profileStr));
          if (p) dispatch(setUser(p));
        } catch (e) {
          console.error("TokenHandler: failed to parse profile param", e);
        }
      }
    } catch (e) {
      console.error("TokenHandler error", e);
    }

    // remove query params from URL
    navigate(location.pathname, { replace: true });
  }, [location.search, navigate, dispatch, location.pathname]);

  return null;
};

const App = () => {
  const toastRef = useRef(null);

  const { data: version, isSuccess } = useGetVersionQuery();

  const showToast = (severity, summary, detail) => {
    if (toastRef.current) {
      toastRef.current.show({ severity, summary, detail, life: 3000 });
    }
  };

  return (
    <PrimeReactProvider>
      <BrowserRouter>
        <TokenHandler />
        <ToastContext.Provider value={showToast}>
          <Toast ref={toastRef} />
          <Header />
          <Loader />
          <div
            style={{
              position: "fixed",
              right: 0,
              bottom: 0,
              fontSize: "10px",
              zIndex: 100000,
            }}
          >
            F{process.env.REACT_APP_VERSION}/B{version}
          </div>
          <Routes>
            <Route path="/student/login" element={<StudentLogin />} />
            <Route path="/auth/google" element={<GoogleCallback />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/*" element={<StudentPortal />} />

            <Route path="/login" element={<Login />} />

            <Route
              path="/station/:stationId/status"
              element={<StatusScreen />}
            />

            <Route
              path="/app/chef/*"
              element={
                <PrivateRoute allowedRoles={["chef", "admin", "manager"]}>
                  <ChefPortal />
                </PrivateRoute>
              }
            />
            <Route
              path="/app/manager/*"
              element={
                <PrivateRoute allowedRoles={["manager", "admin"]}>
                  <ImportModalProvider>
                    <ManagerPortal />
                  </ImportModalProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="/app/admin/*"
              element={
                <PrivateRoute allowedRoles={["admin"]}>
                  <ImportModalProvider>
                    <AdminDashboard />
                  </ImportModalProvider>
                </PrivateRoute>
              }
            />
          </Routes>
        </ToastContext.Provider>
      </BrowserRouter>
    </PrimeReactProvider>
  );
};

export default App;
